import React, { useState, useEffect } from "react";
import {
  getWithdrawals,
  getWithdrawalStats,
  processWithdrawal,
  AdminWithdrawal,
  WithdrawalStats,
} from "../../services/api/admin/withdrawals";
import {
  subscribeToRoleNotifications,
  NotificationEventData,
} from "../../services/api/realtime";
import { getStoredUser } from "../../services/api/auth";
import {
  BanknoteIcon,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  Search,
  RefreshCw,
  AlertCircle,
} from "lucide-react";
import Swal from "sweetalert2";
import Pagination from "../Pagination";

export const WithdrawalManager: React.FC = () => {
  const [withdrawals, setWithdrawals] = useState<AdminWithdrawal[]>([]);
  const [stats, setStats] = useState<WithdrawalStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<string>("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({
    total: 0,
    lastPage: 1,
    currentPage: 1,
    perPage: 20,
  });

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [withdrawalData, statsData] = await Promise.all([
        getWithdrawals(page, filter),
        getWithdrawalStats(),
      ]);
      setWithdrawals(withdrawalData.data);
      setMeta(withdrawalData.meta);
      setStats(statsData);
    } catch (error) {
      console.error("Failed to fetch withdrawals:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, filter]);

  // Subscribe to realtime notifications for withdrawal requests
  useEffect(() => {
    const user = getStoredUser();
    if (!user) return;

    const unsubscribe = subscribeToRoleNotifications(
      user.role,
      (data: NotificationEventData) => {
        // Refresh data when new withdrawal notification comes in
        if (data.type === "withdrawal_request") {
          fetchData();
        }
      }
    );

    return () => unsubscribe();
  }, [page, filter]);

  const handleProcess = async (
    id: string,
    status: "processing" | "completed" | "rejected"
  ) => {
    const statusText =
      status === "completed"
        ? "menyetujui"
        : status === "rejected"
        ? "menolak"
        : "memproses";

    const { isConfirmed, value: adminNotes } = await Swal.fire({
      title: `Konfirmasi ${statusText} withdraw?`,
      input: status === "rejected" ? "textarea" : undefined,
      inputLabel: status === "rejected" ? "Alasan penolakan" : undefined,
      inputPlaceholder: "Masukkan alasan...",
      showCancelButton: true,
      confirmButtonText: `Ya, ${statusText}`,
      cancelButtonText: "Batal",
      confirmButtonColor: status === "rejected" ? "#EF4444" : "#4F46E5",
    });

    if (!isConfirmed) return;

    // Only pass adminNotes if it's a non-empty string (not true/false boolean)
    const notes =
      typeof adminNotes === "string" && adminNotes.trim()
        ? adminNotes.trim()
        : undefined;

    try {
      await processWithdrawal(id, {
        status,
        adminNotes: notes,
      });
      Swal.fire("Berhasil!", `Withdraw berhasil di${statusText}`, "success");
      fetchData();
    } catch (err: any) {
      Swal.fire("Gagal", err?.message || "Gagal memproses withdraw", "error");
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return (
          <span className="px-3 py-1 rounded-full text-[9px] font-black uppercase bg-amber-50 text-amber-600 border border-amber-100">
            Menunggu
          </span>
        );
      case "processing":
        return (
          <span className="px-3 py-1 rounded-full text-[9px] font-black uppercase bg-blue-50 text-blue-600 border border-blue-100">
            Diproses
          </span>
        );
      case "completed":
        return (
          <span className="px-3 py-1 rounded-full text-[9px] font-black uppercase bg-emerald-50 text-emerald-600 border border-emerald-100">
            Selesai
          </span>
        );
      case "rejected":
        return (
          <span className="px-3 py-1 rounded-full text-[9px] font-black uppercase bg-red-50 text-red-600 border border-red-100">
            Ditolak
          </span>
        );
      default:
        return null;
    }
  };

  if (isLoading && !withdrawals.length) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-10 h-10 text-indigo-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-amber-50 rounded-xl">
              <Clock className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">
                Menunggu Proses
              </p>
              <p className="text-2xl font-black text-gray-900">
                {stats?.pending.count || 0}
              </p>
              <p className="text-xs text-amber-600 font-bold">
                Rp {(stats?.pending.amount || 0).toLocaleString("id-ID")}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-50 rounded-xl">
              <RefreshCw className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">
                Sedang Diproses
              </p>
              <p className="text-2xl font-black text-gray-900">
                {stats?.processing.count || 0}
              </p>
              <p className="text-xs text-blue-600 font-bold">
                Rp {(stats?.processing.amount || 0).toLocaleString("id-ID")}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-emerald-50 rounded-xl">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">
                Total Dibayar
              </p>
              <p className="text-2xl font-black text-gray-900">
                {stats?.completed.count || 0}
              </p>
              <p className="text-xs text-emerald-600 font-bold">
                Rp {(stats?.completed.amount || 0).toLocaleString("id-ID")}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter & Table */}
      <div className="bg-white rounded-[2rem] shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-5 border-b border-gray-100 flex flex-col md:flex-row justify-between items-center gap-4 bg-gray-50/30">
          <h3 className="font-black text-gray-900 uppercase tracking-widest text-sm">
            Riwayat Withdraw Mitra
          </h3>
          <div className="flex gap-2">
            {["", "pending", "processing", "completed", "rejected"].map((f) => (
              <button
                key={f}
                onClick={() => {
                  setFilter(f);
                  setPage(1);
                }}
                className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all ${
                  filter === f
                    ? "bg-indigo-600 text-white"
                    : "bg-white text-gray-500 border border-gray-200 hover:bg-gray-50"
                }`}
              >
                {f === ""
                  ? "Semua"
                  : f === "pending"
                  ? "Menunggu"
                  : f === "processing"
                  ? "Proses"
                  : f === "completed"
                  ? "Selesai"
                  : "Ditolak"}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-slate-50 text-gray-400 font-black uppercase text-[9px] tracking-[0.2em]">
              <tr>
                <th className="px-6 py-4">Mitra</th>
                <th className="px-6 py-4">Jumlah</th>
                <th className="px-6 py-4">Rekening Tujuan</th>
                <th className="px-6 py-4">Tanggal</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-center">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {withdrawals.length ? (
                withdrawals.map((w) => (
                  <tr key={w.id} className="hover:bg-gray-50/50">
                    <td className="px-6 py-4">
                      <div className="font-black text-gray-900 text-xs uppercase">
                        {w.partner?.user?.fullName || "-"}
                      </div>
                      <div className="text-[10px] text-gray-400">
                        {w.partner?.user?.email}
                      </div>
                      <div className="text-[10px] text-indigo-500 font-bold">
                        {w.partner?.tier}
                      </div>
                    </td>
                    <td className="px-6 py-4 font-black text-indigo-600 text-base">
                      Rp {w.amount.toLocaleString("id-ID")}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-xs font-bold text-gray-800">
                        {w.bankName}
                      </div>
                      <div className="text-[10px] text-gray-400 font-mono">
                        {w.bankAccountNumber}
                      </div>
                      <div className="text-[10px] text-gray-400">
                        a.n {w.bankAccountName}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-xs font-bold text-gray-900">
                        {new Date(w.createdAt).toLocaleDateString("id-ID", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })}
                      </div>
                      <div className="text-[10px] text-gray-400">
                        {new Date(w.createdAt).toLocaleTimeString("id-ID", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(w.status)}
                      {w.adminNotes && (
                        <div
                          className="text-[10px] text-gray-400 mt-1 max-w-[150px] truncate"
                          title={w.adminNotes}
                        >
                          {w.adminNotes}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-1">
                        {w.status === "pending" && (
                          <>
                            <button
                              onClick={() => handleProcess(w.id, "processing")}
                              className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                              title="Proses"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleProcess(w.id, "completed")}
                              className="p-2 bg-emerald-50 text-emerald-600 rounded-lg hover:bg-emerald-100 transition-colors"
                              title="Setujui"
                            >
                              <CheckCircle2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleProcess(w.id, "rejected")}
                              className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                              title="Tolak"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {w.status === "processing" && (
                          <>
                            <button
                              onClick={() => handleProcess(w.id, "completed")}
                              className="p-2 bg-emerald-50 text-emerald-600 rounded-lg hover:bg-emerald-100 transition-colors"
                              title="Selesai"
                            >
                              <CheckCircle2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleProcess(w.id, "rejected")}
                              className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                              title="Tolak"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {(w.status === "completed" ||
                          w.status === "rejected") && (
                          <span className="text-[10px] text-gray-400">-</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-6 py-16 text-center">
                    <BanknoteIcon className="w-12 h-12 text-gray-100 mx-auto mb-4" />
                    <p className="text-[10px] text-gray-300 font-black uppercase tracking-[0.4em]">
                      Tidak Ada Data Withdraw
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {meta.lastPage > 1 && (
          <div className="p-4 border-t border-gray-100">
            <Pagination
              currentPage={meta.currentPage}
              totalPages={meta.lastPage}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>
    </div>
  );
};
